import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, UtensilsCrossed, Plus, Minus, ShoppingBag, CreditCard, Banknote, Check, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { api } from '../App';

const CATEGORIES = [
  { key: 'entree', label: 'Entrees' },
  { key: 'side', label: 'Sides' },
  { key: 'drink', label: 'Drinks' },
  { key: 'dessert', label: 'Desserts' },
];

const PreOrderPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState({});
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingOrder, setExistingOrder] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bookingRes, menuRes, orderRes] = await Promise.all([
          api.get(`/bookings/${bookingId}`),
          api.get('/menu'),
          api.get(`/food-orders/booking/${bookingId}`)
        ]);
        setBooking(bookingRes.data);
        setMenu(menuRes.data);
        if (orderRes.data.has_order) {
          setExistingOrder(orderRes.data.order);
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Could not load booking details');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [bookingId, navigate]);

  const updateCart = (item, delta) => {
    setCart(prev => {
      const current = prev[item.id]?.quantity || 0;
      const newQty = Math.max(0, current + delta);
      if (newQty === 0) {
        const { [item.id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [item.id]: { ...item, quantity: newQty } };
    });
  };

  const cartItems = Object.values(cart);
  const cartTotal = cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const cartCount = cartItems.reduce((sum, i) => sum + i.quantity, 0);

  const handleSubmit = async () => {
    if (cartItems.length === 0) {
      toast.error('Please add at least one item to your order');
      return;
    }
    setSubmitting(true);
    try {
      const items = cartItems.map(i => ({
        menu_item_id: i.id,
        name: i.name,
        price: i.price,
        quantity: i.quantity
      }));
      const res = await api.post('/food-orders', {
        booking_id: bookingId,
        items,
        payment_method: paymentMethod,
        special_instructions: specialInstructions || null
      });
      toast.success(res.data.message);
      navigate(`/confirmation/${bookingId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!booking) return null;

  // Already has an order
  if (existingOrder) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-white border-b border-border">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <Link to={`/confirmation/${bookingId}`} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-5 h-5" /> Back to Booking
            </Link>
          </div>
        </header>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-serif text-2xl font-bold mb-2">Food Pre-Order Confirmed</h1>
            <p className="text-muted-foreground">Your meal will be ready when you arrive at The Village!</p>
          </div>
          <div className="bg-white rounded-2xl border border-border p-6">
            <h3 className="font-semibold mb-4">Your Order</h3>
            <div className="space-y-2 mb-4">
              {existingOrder.items.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span>{item.quantity}x {item.name}</span>
                  <span className="font-medium">BSD ${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-3 flex justify-between font-bold">
              <span>Total</span>
              <span>BSD ${existingOrder.total.toFixed(2)}</span>
            </div>
            <div className="mt-3 text-sm text-muted-foreground">
              Payment: {existingOrder.payment_method === 'cash' ? 'Cash on Arrival' : 'Credit / Debit Card'}
            </div>
            {existingOrder.special_instructions && (
              <div className="mt-3 text-sm text-muted-foreground">
                Notes: {existingOrder.special_instructions}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to={`/confirmation/${bookingId}`} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-booking">
            <ArrowLeft className="w-5 h-5" /> Back to Booking
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <UtensilsCrossed className="w-7 h-7 text-primary" />
          </div>
          <h1 className="font-serif text-3xl font-bold" data-testid="preorder-title">Pre-Order Your Meal</h1>
          <p className="text-muted-foreground mt-2">
            Order ahead and your food will be freshly prepared and ready when you arrive at The Village
          </p>
        </div>

        {/* Time Notice */}
        <div className="flex items-start gap-3 mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm max-w-2xl mx-auto" data-testid="time-notice">
          <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Pre-orders must be placed at least 1 hour before your tour departure.</p>
            <p>Your tour: {booking.date} at {booking.time_slot}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Menu */}
          <div className="lg:col-span-2 space-y-8">
            {CATEGORIES.map(cat => {
              const items = menu.filter(i => i.category === cat.key);
              if (items.length === 0) return null;
              return (
                <div key={cat.key}>
                  <h2 className="font-serif text-xl font-bold mb-4 text-foreground" data-testid={`category-${cat.key}`}>{cat.label}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {items.map(item => {
                      const qty = cart[item.id]?.quantity || 0;
                      return (
                        <div key={item.id} className={`bg-white rounded-xl border p-4 transition-all ${qty > 0 ? 'border-primary shadow-sm' : 'border-border'}`} data-testid={`menu-item-${item.id}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h3 className="font-semibold text-sm">{item.name}</h3>
                              {item.description && (
                                <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                              )}
                            </div>
                            <span className="font-bold text-primary ml-3 text-sm">BSD ${item.price.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-end gap-2 mt-3">
                            {qty > 0 && (
                              <>
                                <button
                                  onClick={() => updateCart(item, -1)}
                                  className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                                  data-testid={`decrease-${item.id}`}
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span className="w-8 text-center font-semibold text-sm" data-testid={`qty-${item.id}`}>{qty}</span>
                              </>
                            )}
                            <button
                              onClick={() => updateCart(item, 1)}
                              className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90 transition-colors"
                              data-testid={`increase-${item.id}`}
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary Sidebar */}
          <div>
            <div className="bg-white rounded-2xl border border-border p-6 sticky top-8">
              <div className="flex items-center gap-2 mb-4">
                <ShoppingBag className="w-5 h-5 text-primary" />
                <h2 className="font-serif text-lg font-bold">Your Order</h2>
                {cartCount > 0 && (
                  <span className="bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cartCount}</span>
                )}
              </div>

              {cartItems.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Add items from the menu</p>
              ) : (
                <div className="space-y-2 mb-4">
                  {cartItems.map(item => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span>{item.quantity}x {item.name}</span>
                      <span className="font-medium">BSD ${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-border pt-3 flex justify-between font-bold">
                    <span>Total</span>
                    <span className="text-primary" data-testid="order-total">BSD ${cartTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Special Instructions */}
              <div className="mb-4">
                <label className="text-sm font-medium block mb-1">Special Instructions</label>
                <Textarea
                  placeholder="Allergies, dietary needs, etc."
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  className="text-sm"
                  rows={2}
                  data-testid="special-instructions"
                />
              </div>

              {/* Payment Method */}
              <div className="mb-5">
                <label className="text-sm font-medium block mb-2">Payment Method</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setPaymentMethod('cash')}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      paymentMethod === 'cash' ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                    data-testid="pay-cash"
                  >
                    <Banknote className="w-5 h-5 mx-auto mb-1 text-amber-600" />
                    <span className="text-xs font-medium block">Cash</span>
                    <span className="text-[10px] text-muted-foreground">Pay on arrival</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('card')}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      paymentMethod === 'card' ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                    data-testid="pay-card"
                  >
                    <CreditCard className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                    <span className="text-xs font-medium block">Card</span>
                    <span className="text-[10px] text-muted-foreground">Credit / Debit</span>
                  </button>
                </div>
              </div>

              <Button
                className="w-full btn-primary py-5"
                disabled={submitting || cartItems.length === 0}
                onClick={handleSubmit}
                data-testid="place-order-btn"
              >
                {submitting ? 'Placing Order...' : `Place Order - BSD $${cartTotal.toFixed(2)}`}
              </Button>

              <p className="text-[10px] text-muted-foreground text-center mt-3">
                {paymentMethod === 'cash'
                  ? 'You will pay for your food order in cash when you arrive.'
                  : 'Card payment will be processed when available. Otherwise, pay on arrival.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreOrderPage;
